import { Directive, ElementRef, EventEmitter, Input, Output } from '@angular/core';
import { PropertyChangedEventArgs } from '@mosaik/presentation';
import { AnyExtensions } from '../../../../Extensions/AnyExtensions';
import { ElementComponent } from '../../Abstracts/ElementComponent';

/**
 * @public
 * @abstract
 */
@Directive()
export abstract class Range extends ElementComponent {
  private _minimum: number;
  private _maximum: number;
  private _value: number;
  private readonly _valueChanged: EventEmitter<PropertyChangedEventArgs<number>>;

  protected constructor(element: ElementRef<HTMLElement>) {
    super(element);

    this._minimum = 0;
    this._maximum = 1;
    this._value = 0;
    this._valueChanged = new EventEmitter<PropertyChangedEventArgs<number>>();
  }

  /**
   * Minimum restricts the minimum value of the `value` property.
   * @public
   * @type number
   * @default 0
   */
  @Input()
  public get minimum(): number {
    return this._minimum;
  }
  public set minimum(value: number) {
    this._minimum = value;
  }

  /**
   * Maximum restricts the maximum value of the `value` property.
   * @public
   * @type number
   * @default 1
   */
  @Input()
  public get maximum(): number {
    return this._maximum;
  }
  public set maximum(value: number) {
    this._maximum = value;
  }

  /**
   * Specifies the value of the `Range`.
   * @public
   * @type number
   * @default 0
   */
  @Input()
  public get value(): number {
    return this._value;
  }
  public set value(value: number) {
    const current = AnyExtensions.coerceNumber(value);
    if (this._value !== current) {
      this.onValueChanged(this._value, (this._value = current));
    }
  }

  /**
   * Called when the value is changed.
   * Provides reference to the `PropertyChangedEventArgs` with old and new value as event argument.
   * @public
   * @readonly
   * @type EventEmitter<PropertyChangedEventArgs<number>>
   * @default EventEmitter
   */
  @Output()
  public get valueChanged(): EventEmitter<PropertyChangedEventArgs<number>> {
    return this._valueChanged;
  }

  /**
   * This method is invoked when the Value property changes.
   * @protected
   */
  protected onValueChanged(oldValue: number, newValue: number): void {
    const args = new PropertyChangedEventArgs<number>(oldValue, newValue);
    this._valueChanged.emit(args);
  }
}
